import { DialogComponent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';

/**
 * @typedef {object} GiftMediaRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} track - The horizontal scroll-snap list of full-size images.
 * @property {HTMLElement[]} slides - One slide per image.
 * @property {HTMLElement} [thumbnails] - The thumbnail strip (only rendered for 2+ images).
 * @property {HTMLButtonElement[]} [thumbnailButtons] - One button per image.
 * @property {HTMLButtonElement} [previousButton] - Desktop "previous" arrow.
 * @property {HTMLButtonElement} [nextButton] - Desktop "next" arrow.
 */

/**
 * Full-screen preview of the Buy X Get Y gift's images (blocks/buy-x-get-y.liquid).
 *
 * A `DialogComponent` subclass rather than a nested one, so `/select`, `/previous` and `/next`
 * resolve to the same element as `/showDialog` (declarative events go to the closest component).
 * The images sit in a horizontal scroll-snap track, so swiping works natively on touch; the
 * thumbnails and arrows only scroll that track, and the active index is read back from the scroll
 * position — one source of truth whether the shopper swiped, clicked or used the arrow keys.
 */
class GiftMediaDialog extends DialogComponent {
  #activeIndex = 0;
  #scrollFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('click', this.#handleTrackClick);
    this.refs.dialog.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('click', this.#handleTrackClick);
    this.refs.dialog.removeEventListener('scroll', this.#handleScroll, { capture: true });
    cancelAnimationFrame(this.#scrollFrame);
  }

  /** @returns {GiftMediaRefs} */
  get #refs() {
    return /** @type {GiftMediaRefs} */ (/** @type {unknown} */ (this.refs));
  }

  /**
   * Opens on the first image every time. Queued after the base class's own frame, which is the
   * one that calls `showModal()` — the track has no width to scroll until the dialog is open.
   */
  showDialog() {
    super.showDialog();
    requestAnimationFrame(() => this.#show(0, 'instant'));
  }

  /**
   * Thumbnail click (`on:click="/select/<index>"`). Takes only the index: the declarative event
   * system appends the event as the last argument, so nothing optional may follow it here.
   * @param {number} index - The image to show.
   */
  select(index) {
    this.#show(index);
  }

  previous() {
    this.#show(Math.max(this.#activeIndex - 1, 0));
  }

  next() {
    this.#show(Math.min(this.#activeIndex + 1, this.#refs.slides.length - 1));
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #show(index, behavior = prefersReducedMotion() ? 'instant' : 'smooth') {
    const { track, slides } = this.#refs;
    const slide = slides[index];
    if (!slide) return;

    track.scrollTo({ left: slide.offsetLeft, behavior });
    this.#setActive(index);
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    this.#activeIndex = index;

    const { thumbnails, thumbnailButtons = [], previousButton, nextButton, slides } = this.#refs;

    thumbnailButtons.forEach((button, i) => {
      const isActive = i === index;
      if (button.hasAttribute('aria-current') === isActive) return;
      button.toggleAttribute('aria-current', isActive);
      if (isActive && thumbnails) scrollIntoView(button, { ancestor: thumbnails, behavior: 'instant', block: 'center', inline: 'center' });
    });

    if (previousButton) previousButton.disabled = index === 0;
    if (nextButton) nextButton.disabled = index === slides.length - 1;
  }

  /** Keeps the thumbnails in step with a swipe. */
  #handleScroll = (/** @type {Event} */ event) => {
    if (event.target !== this.#refs.track) return;

    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      const { track } = this.#refs;
      if (!track.clientWidth) return;

      const index = Math.round(track.scrollLeft / track.clientWidth);
      if (index !== this.#activeIndex) this.#setActive(index);
    });
  };

  /**
   * Arrow keys page through the images. Escape is already handled by the base class.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!this.refs.dialog.open) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  };

  /**
   * The dialog fills the viewport, so the base class's click-outside test can never pass. A click
   * on a slide's empty area (anything but the photo itself) closes instead, like a backdrop would.
   * @param {MouseEvent} event
   */
  #handleTrackClick = (event) => {
    if (!this.refs.dialog.open) return;

    const target = /** @type {Element | null} */ (event.target);
    if (target?.matches('[ref="slides[]"]')) this.closeDialog();
  };
}

if (!customElements.get('gift-media-dialog')) {
  customElements.define('gift-media-dialog', GiftMediaDialog);
}
